import React, { useState, FunctionComponent } from 'react';

import Notification from 'components/notification/Notification';
import NotificationView from 'components/notification/NotificationView';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { Divider, TablePagination, CardContent, Card, Stack, Drawer, Typography,
  Accordion, AccordionSummary, AccordionDetails, Menu, MenuItem,
  TextField, InputAdornment, Button

 } from '@mui/material';

import { NotificationModel } from 'model/notification/NotificationModels';
import SendIcon from '@mui/icons-material/Send';
import { Toolbar } from 'components/common/Toolbar';
import { SEARCH, GROUP_BY } from 'assets/Icons'
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';


import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import SortIcon from '@mui/icons-material/Sort';
import DoneIcon from '@mui/icons-material/Done';

import {findClosestNumber} from 'utils/Common';

export class NotificationListProps {
  notifications: NotificationModel[];
}

/* TODO: 
1. The service should send smaller objects to the view
2. It is re-rendering when hovering over
*/
export const NotificationList: FunctionComponent<NotificationListProps> = ({notifications}) => {
  const [filter, setFilter] = useState<string>(""); // Search input
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [groupBy, setGroupBy] = useState(""); // Grouping by Role
  const [notification, setNotification] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // sm = mobile
  
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [groupByMenuAnchorEl, setGroupByMenuAnchorEl] = useState(null);
  const groupByMenuOpen = Boolean(groupByMenuAnchorEl);

  const handleGroupByClick = (event: any) => {
      setGroupByMenuAnchorEl(event.currentTarget);
  };

  const handleGroupByMenuClose = (sellection: string) => {
      if (sellection === "ignore") {
        setGroupByMenuAnchorEl(null);
        setRowsPerPage(25);
        return;
      }
      setGroupBy(sellection);
      setGroupByMenuAnchorEl(null);
      setRowsPerPage(findClosestNumber([10, 25, 50], filteredData.length));
  };

  const [sortByMenuAnchorEl, setSortByMenuAnchorEl] = useState(null);
  const sortByMenuOpen = Boolean(sortByMenuAnchorEl);

  const handleSortByClick = (event: any) => {
      setSortByMenuAnchorEl(event.currentTarget);
  };

  const handleSortByMenuClose = (sellection: string) => {
      if (sellection === "ignore") {
        setSortByMenuAnchorEl(null);
        return;
      }
      setSortOrder(sortBy === sellection && sortOrder === "asc" ? "desc" : "asc");
      setSortBy(sellection);
      setSortByMenuAnchorEl(null);
  };

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };


  // Handle search input
  const handleFilterChange = (event: any) => {
      setFilter(event.target.value.toLowerCase());
      setPage(0);
  };

  // Handle pagination change
  const handleChangePage = (event: any, newPage: any) => {
      setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: any) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
  };

  const handleView = (notificationId: any) => {
    setDrawerOpen(!drawerOpen);
    setNotification(notificationId);
  }

  // Filter the data
  let filteredData = notifications.filter(
      (row: NotificationModel) =>
          row.recipient.toLowerCase().includes(filter) ||
          row.title.toLowerCase().includes(filter) ||
          row.amount.toString().includes(filter)
  );

  // Sort the data
  filteredData.sort((a: NotificationModel, b: NotificationModel) => {
      if (a[sortBy as keyof NotificationModel] < b[sortBy as keyof NotificationModel]) return sortOrder === "asc" ? -1 : 1;
      if (a[sortBy as keyof NotificationModel] > b[sortBy as keyof NotificationModel]) return sortOrder === "asc" ? 1 : -1;
      return 0;
  });

  // Grouping the data
  const groupedData = filteredData.reduce((acc: Record<string, NotificationModel[]>, item: NotificationModel) => {
            (acc[item[groupBy as keyof NotificationModel]] = acc[item[groupBy as keyof NotificationModel]] || []).push(item);
            return acc;
        }, {});

  const toolbarItemsDesktop = [
    <Button variant='contained' key="1" color="inherit" onClick={handleSortByClick}><SortIcon aria-hidden="true" />&nbsp;Sort</Button>,
    <Button variant='contained' key="2" color="inherit" onClick={handleGroupByClick}>{GROUP_BY(18)}&nbsp;Group</Button>,
    <TextField key="3" style={{width: "212px"}} size="small" onChange={handleFilterChange}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            {SEARCH}
          </InputAdornment>
        ), 
      }}
    />
  ]

  const toolbarItemsMobile = [
    <Button aria-label="Sort" variant='contained' key="1" color="inherit" sx={{maxWidth: "50px !important", minWidth: "50px !important", width: "50px !important"}} onClick={handleSortByClick}>
      <SortIcon aria-hidden="true" />
    </Button>,
    <Button aria-label="Group by" variant='contained' key="2" color="inherit" sx={{maxWidth: "50px !important", minWidth: "50px !important", width: "50px !important"}} onClick={handleGroupByClick}>
      {GROUP_BY(18)}
    </Button>,
    <TextField key="3" style={{width: "210px"}} size="small" onChange={handleFilterChange}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            {SEARCH}
          </InputAdornment>
        ), 
      }}
    />
  ]
  
  return (<>
    <Stack spacing={1}>
      <Toolbar items={isMobile? toolbarItemsMobile : toolbarItemsDesktop} 
        icon={<SendIcon aria-hidden="true" style={{width: "24px", marginBottom: "-4px", marginRight: "10px"}}/>}
        title='NOTIFICATIONS'/>
        
      <Card variant='outlined'>
        <CardContent>
        {Object.entries(groupedData).length === 0 ? 
            <Typography className='wizard-subtitle-color' variant="body1" gutterBottom 
              sx={{ wordBreak: "break-word", textAlign: "center", marginTop: "12px" }} marginBottom={1}>
              No records found.
            </Typography> : 
          groupBy?
            Object.entries(groupedData).map(([group, rows]) => (
              <div style={{marginBottom: "5px"}}>
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon aria-hidden="true" />}
                  aria-controls="panel1-content"
                  id="panel1-header">
                  <Typography component="span">{group}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                {rows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((notification: NotificationModel, index: any) => (
                    <div key={notification.id + index}>
                      <NotificationView notification={notification} onClick={() => handleView(notification)}/>
                      <Divider sx={{mb: 1, mt: 1}}/>
                    </div>
                  ))}
                </AccordionDetails>
              </Accordion>
              </div>
            )):filteredData
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((notification: NotificationModel, index: any) => (
                <div key={notification.id + index}>
                  <NotificationView notification={notification} onClick={() => handleView(notification)}/>
                  <Divider sx={{mb: 1, mt: 1}}/>
                </div>
            ))
        }
        </CardContent>
      </Card>
      {groupBy?<></>:
        <Card variant='outlined'>
          <CardContent style={{marginBottom: "0", padding: "0"}}>
            <TablePagination
              component="div"
              count={filteredData.length}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handleChangePage}
              rowsPerPageOptions={[10, 25, 50]}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </CardContent>
        </Card>}
    </Stack>
  
    <Drawer anchor="right"
            container={window.document.body}
            variant="temporary"
            open={drawerOpen}
            onClose={handleDrawerToggle}
            // ModalProps={{
            //   keepMounted: true, // Better open performance on mobile.
            // }}
            sx={{
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: "340px"}, 
            }}>
      <Card sx={{overflow: "auto !important", height: "100vh" }}>
        <CardContent>
          <Notification notification={notification} onClose={handleDrawerToggle}/>
        </CardContent>
      </Card>
    </Drawer>

    <Menu anchorEl={groupByMenuAnchorEl} open={groupByMenuOpen} onClose={()=>handleGroupByMenuClose("ignore")}>
      <MenuItem onClick={() => handleGroupByMenuClose("recipient")}>
        Recipient&nbsp;{groupBy === "recipient"?<DoneIcon/>:<></>}
      </MenuItem>
      <MenuItem onClick={() => handleGroupByMenuClose("title")}>
        Title&nbsp;{groupBy === "title"?<DoneIcon/>:<></>}
      </MenuItem>
      {!groupBy?<></>:<MenuItem onClick={() => handleGroupByMenuClose(null)}>
        (None)
      </MenuItem>}
    </Menu>

    <Menu anchorEl={sortByMenuAnchorEl} open={sortByMenuOpen} onClose={()=>handleSortByMenuClose("ignore")}>
      <MenuItem onClick={() => handleSortByMenuClose("sentDate")}>
        Sent Date&nbsp;{sortBy === "sentDate"?sortOrder === "asc"?<KeyboardArrowUpIcon/>:<KeyboardArrowDownIcon/>:<></>}
      </MenuItem>
      <MenuItem onClick={() => handleSortByMenuClose("recipient")}>
        Recipient&nbsp;{sortBy === "recipient"?sortOrder === "asc"?<KeyboardArrowUpIcon/>:<KeyboardArrowDownIcon/>:<></>}
      </MenuItem>
      <MenuItem onClick={() => handleSortByMenuClose("title")}>
        Title&nbsp;{sortBy === "title"?sortOrder === "asc"?<KeyboardArrowUpIcon/>:<KeyboardArrowDownIcon/>:<></>}
      </MenuItem>
      <MenuItem onClick={() => handleSortByMenuClose("amount")}>
        Amount&nbsp;{sortBy === "amount"?sortOrder === "asc"?<KeyboardArrowUpIcon/>:<KeyboardArrowDownIcon/>:<></>}
      </MenuItem>
      {!sortBy?<></>:<MenuItem onClick={() => handleSortByMenuClose(null)}>
        (None)
      </MenuItem>}
    </Menu>

  </>);
}
export default NotificationList;